/**
 * Hook to check if the current user is an internal admin
 * Uses the is_internal_admin database function
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// FIX-R130-CRIT-1: queryKey MUST include the current user id so cached
// admin status from a previous session cannot bleed into the next user.
// Previously the key was ['is-internal-admin'] with no user binding —
// combined with QueryClient surviving signOut, User A (internal admin)
// signing out and User B signing in would see User B treated as admin
// for up to staleTime (60s), because the cached `true` value matched
// the bare key. The AuthContext effect that layers admin exposure-flag
// overrides keys off this hook's value, so the cache leak grants real
// UI to the wrong user.
//
// ARC-A4: pre-A4 this hook owned its own `supabase.auth.getUser()` +
// `onAuthStateChange` listener — a duplicate of AuthContext's canonical
// listener that on top of the duplication formed a feedback loop with
// AuthContext (which consumes this hook at L104 of AuthContext.tsx).
// Post-A4 the user.id input is sourced from AuthContext via two
// surfaces:
//
//   • `useIsInternalAdminForUserId(userId)` — the variant the canonical
//     caller (AuthContext) uses. AuthContext renders ABOVE its own
//     `<AuthContext.Provider value={...}>` JSX, so calling `useAuth()`
//     from inside this hook AT THAT CALLSITE would throw (the
//     `useContext(AuthContext)` lookup returns undefined for
//     components rendered above their own Provider). The userId-arg
//     variant lets AuthContext pass `user?.id ?? null` directly.
//
//   • `useIsInternalAdmin()` — the public API for any component that
//     sits INSIDE AuthProvider (the 13+ non-AuthContext callers:
//     TopBar, InternalAdminRoute, EveVoiceShell, TrainingCatalogAdmin,
//     PassportHome, OrgRecommendations, FeatureFlagsDebug,
//     FeatureFlagsAdmin, DiagnosticFlowAdmin, DecoupledUsers,
//     ConnectPage, BetaControlPanel). It thin-wraps the userId variant
//     by reading user.id from AuthContext.
//
// Listener count: pre-A4 = 1 inside this hook + 1 in AuthContext.
// Post-A4 = 0 inside this hook + 1 in AuthContext (canonical owner).
//
// Behavior preservation:
//   - queryKey shape unchanged: ['is-internal-admin', userId ?? 'anon']
//   - RPC name + args unchanged: `is_internal_admin({_user_id: userId})`
//   - staleTime unchanged: 60_000 ms
//   - error handling unchanged: console.error + return false on RPC error
//   - return shape unchanged: useQuery result with `data: boolean`
//   - feedback loop with AuthContext eliminated (the hook no longer
//     spawns its own listener that fires alongside AuthContext's
//     listener on every auth event)

export function useIsInternalAdminForUserId(userId: string | null) {
  return useQuery({
    // user-id-scoped key. When userId is null we use a sentinel so the
    // value cannot collide with any signed-in user's cached entry.
    queryKey: ['is-internal-admin', userId ?? 'anon'],
    queryFn: async () => {
      if (!userId) return false;

      const { data, error } = await supabase.rpc('is_internal_admin', {
        _user_id: userId,
      });

      if (error) {
        console.error('Failed to check internal admin:', error);
        return false;
      }
      return data === true;
    },
    staleTime: 60000, // Cache for 1 minute
  });
}

export function useIsInternalAdmin() {
  const { user } = useAuth();
  return useIsInternalAdminForUserId(user?.id ?? null);
}
